/**
 * FilePondManager - نظام إدارة تحميل الملفات المتقدم
 * يدعم تكوينات متعددة من FilePond بشكل مستقل
 */

import { FilePond } from "./plugins";

class FilePondManager {
    constructor() {
        this.instances = new Map();
        this.globalConfig = {
            labelIdle: this._getLocalizedLabelText(),
            credits: false,
            allowDrop: true,
            allowBrowse: true,
            allowImagePreview: true,
            imagePreviewHeight: 80,
            // Client-side resize/transform is opt-in per instance (camera flows
            // only); off here so every other uploader keeps sending originals.
            allowImageResize: false,
            allowImageTransform: false,
            acceptedFileTypes: [
                "image/png",
                "image/jpeg",
                "image/jpg",
                "image/webp",
            ],
        };
    }

    /**
     * الحصول على نص الترجمة المناسب حسب اتجاه الصفحة
     */
    _getLocalizedLabelText() {
        const isRTL = typeof KTUtil !== "undefined" && KTUtil.isRTL();
        const actionText1 = isRTL
            ? "قم بسحب وإفلات الملفات أو"
            : "Drag & Drop your files or";
        const actionText2 = isRTL ? "تصفح" : "browse";
        return `${actionText1} <span class='filepond--label-action'>${actionText2}</span>`;
    }

    /**
     * الحصول على رمز CSRF
     */
    _getCsrfToken() {
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        return metaTag ? metaTag.getAttribute("content") : "";
    }

    /**
     * إنشاء إعدادات الخادم لتحميل الملفات
     */
    _createServerConfig(uploadRoute, deleteRoute, storePath) {
        return {
            process: (
                fieldName,
                file,
                metadata,
                load,
                error,
                progress,
                abort,
            ) => {
                const xhr = new XMLHttpRequest();
                const formData = new FormData();

                formData.append("image", file);
                formData.append("store_path", storePath);

                xhr.open("POST", uploadRoute, true);
                xhr.setRequestHeader("X-CSRF-TOKEN", this._getCsrfToken());

                xhr.upload.onprogress = (e) => {
                    if (e.lengthComputable) {
                        progress((e.loaded / e.total) * 100);
                    }
                };

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        const response = JSON.parse(xhr.responseText);
                        load(response.filename, response.filename);
                    } else {
                        let errorMessage = "فشل التحميل";

                        try {
                            const response = JSON.parse(xhr.responseText);
                            if (response && response.message) {
                                errorMessage = response.message;
                            }
                        } catch (e) {
                            console.error("خطأ في تحليل استجابة الخادم:", e);
                        }

                        error(errorMessage);
                    }
                };

                xhr.onerror = () => {
                    error("فشل الاتصال بالخادم");
                };

                xhr.send(formData);

                return {
                    abort: () => {
                        xhr.abort();
                        abort();
                    },
                };
            },
            revert: (filename, load, error) => {
                const xhr = new XMLHttpRequest();

                xhr.open("POST", deleteRoute, true);
                xhr.setRequestHeader("X-CSRF-TOKEN", this._getCsrfToken());
                xhr.setRequestHeader("Content-Type", "application/json");

                const data = JSON.stringify({
                    image: filename,
                    store_path: storePath,
                });

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        load();
                    } else {
                        error("فشل إلغاء التحميل");
                    }
                };

                xhr.onerror = () => {
                    error("فشل إلغاء التحميل");
                };

                xhr.send(data);

                return {
                    abort: () => {
                        xhr.abort();
                    },
                };
            },
        };
    }

    /**
     * تحديث حالة التحميل
     */
    _updateUploadStatus(status) {
        const uploadStatus = document.getElementById("uploadStatus");
        if (uploadStatus) {
            uploadStatus.value = status;
        }
    }

    /**
     * إنشاء ودمج تكوين FilePond
     */
    _createConfig(options, isMultiple, uploadRoute, deleteRoute, storePath) {
        const serverConfig = this._createServerConfig(
            uploadRoute,
            deleteRoute,
            storePath,
        );

        const defaultConfig = {
            ...this.globalConfig,
            allowRemove: true,
            allowReplace: true,
            allowMultiple: isMultiple,
            server: serverConfig,
            onaddfile: (error, file) => {
                // الملف المرفوض لن يُرفع أبداً، فلا يجب أن يترك الحالة على "جاري الرفع"
                if (!error) {
                    this._updateUploadStatus("1");
                }

                if (options.onaddfile) {
                    options.onaddfile(error, file);
                }
            },
            onprocessfile: (error, file) => {
                this._updateUploadStatus("0");

                if (options.onprocessfile) {
                    options.onprocessfile(error, file);
                }
            },
            onerror: (error, file, status) => {
                Swal.fire({
                    icon: "error",
                    title: "خطأ في رفع الملف",
                    text: error.body,
                    confirmButtonText: "حسناً",
                });

                if (options.onerror) {
                    options.onerror(error, file, status);
                }
            },
        };

        if (isMultiple) {
            defaultConfig.allowReorder = true;
        } else {
            defaultConfig.onremovefile = (error, file) => {
                if (options.element) {
                    options.element.value = "";
                }

                if (options.onremovefile) {
                    options.onremovefile(error, file);
                }
            };
        }

        if (
            options.acceptedFileTypes &&
            options.acceptedFileTypes.length === 0
        ) {
            delete options.acceptedFileTypes;
        }

        if (options.captureMethod) {
            defaultConfig.captureMethod = options.captureMethod;
        }

        if (options.allowDrop === false) {
            defaultConfig.allowDrop = false;
        }

        if (options.allowPaste === false) {
            defaultConfig.allowPaste = false;
        }

        return { ...defaultConfig, ...options };
    }

    create(selector, options = {}) {
        const elements = document.querySelectorAll(selector);
        if (!elements.length) {
            console.error(`لم يتم العثور على عناصر للمحدد: ${selector}`);
            return null;
        }

        elements.forEach((element) => {
            // إعادة الإنشاء على نفس العنصر تُنتج FilePond متداخلاً بدل تحديث القائم
            if (this.instances.has(element) || FilePond.find(element)) {
                return;
            }

            const isMultiple = options.allowMultiple || false;
            const uploadRoute = options.uploadRoute || "";
            const deleteRoute = options.deleteRoute || "";
            const storePath = options.storePath || "";

            // دمج الإعدادات
            const config = this._createConfig(
                { ...options, element },
                isMultiple,
                uploadRoute,
                deleteRoute,
                storePath,
            );

            // إنشاء FilePond
            const pond = FilePond.create(element, config);

            // حفظ مرجع FilePond
            this.instances.set(element, pond);
        });

        return this;
    }

    createMultiple(
        selector,
        uploadRoute,
        deleteRoute,
        storePath,
        acceptedFileTypes = [],
    ) {
        return this.create(selector, {
            allowMultiple: true,
            uploadRoute,
            deleteRoute,
            storePath,
            acceptedFileTypes,
        });
    }

    createSingle(
        selector,
        uploadRoute,
        deleteRoute,
        storePath,
        acceptedFileTypes = [],
    ) {
        return this.create(selector, {
            allowMultiple: false,
            uploadRoute,
            deleteRoute,
            storePath,
            acceptedFileTypes,
        });
    }

    /**
     * Enhanced reset functionality for FilePond instances
     * @param {string|Element} selector - CSS selector or DOM element
     * @param {ResetOptions} options - Reset configuration options
     * @returns {FilePondManager} - Returns this for method chaining
     * @throws {Error} - Throws error if selector is invalid
     */
    reset(selector, options = {}) {
        const { revert = true, clear = true } = options;

        try {
            // Handle both string selectors and direct element references
            const elements =
                typeof selector === "string"
                    ? document.querySelectorAll(selector)
                    : [selector].filter((el) => el instanceof Element);

            if (!elements.length) {
                return;
                // throw new Error(`No elements found for selector: ${selector}`);
            }

            elements.forEach((element) => {
                const pond =
                    this.instances.get(element) || FilePond.find(element);

                if (!pond) {
                    console.warn(
                        `No FilePond instance found for element`,
                        element,
                    );
                    return;
                }

                // Perform the reset operations
                if (revert) {
                    pond.removeFiles({ revert: true });
                }

                if (clear) {
                    pond.removeFiles();
                }
            });

            return this;
        } catch (error) {
            console.error("Error during FilePond reset:", error);
            throw error;
        }
    }

    /**
     * Reset all FilePond instances
     * @returns {FilePondManager}
     */
    resetAll() {
        this.instances.forEach((pond, element) => {
            this.reset(element);
        });

        return this;
    }

    /**
     * الحصول على قيم الصور المُحملة
     */
    getValues(paramName) {
        const inputs = document.querySelectorAll(`input[name="${paramName}"]`);
        if (!inputs.length) return null;

        const values = [];

        inputs.forEach((input) => {
            if (input.value) {
                values.push(input.value);
            }
        });

        if (values.length === 0) {
            return "";
        } else if (values.length === 1) {
            return values[0];
        } else {
            return values.join(",");
        }
    }

    getFilesFromInstance(selector) {
        const pond = this.getInstance(selector);
        if (!pond) return null;

        const files = pond.getFiles().filter((file) => file.status === 5); // 5 means PROCESSING_COMPLETE

        return files.map((file) => file.serverId || "").join(",");
    }

    /**
     * الحصول على كائن FilePond
     */
    getInstance(selector) {
        const element = document.querySelector(selector);
        return element ? FilePond.find(element) : null;
    }

    /**
     * تُقرأ الحالة من FilePond نفسه لا من عدّاد داخلي، لأن العدّاد يختل عند رفض
     * ملف أو إلغاء رفعه فيبقى النموذج معلقاً على "انتظر حتى يتم رفع الصور"
     */
    isUploading() {
        const { PROCESSING, PROCESSING_QUEUED } = FilePond.FileStatus;

        return [...this.instances.values()].some((pond) => {
            try {
                return pond
                    .getFiles()
                    .some(
                        (file) =>
                            file.status === PROCESSING ||
                            file.status === PROCESSING_QUEUED,
                    );
            } catch (e) {
                return false;
            }
        });
    }
}

const fileUploader = new FilePondManager();

export { FilePondManager, fileUploader };
